import { Component, ViewEncapsulation, OnInit, Input, ChangeDetectionStrategy, output, inject } from '@angular/core';
import {
  LengthUnit,
  MassUnit,
  SelectItemV2,
  UnbalanceUnit,
  UnitsService,
  UNIT_DEFAULTS,
  FrequencyUnit,
  SelectV2Module,
  RadioV2Module,
  InputV2Module,
  ToggleV2Module,
} from '@schenck/rotec-components';
import { Observable } from 'rxjs';
import { ReportTemplateConfiguration } from 'src/dto/report-template';
import { TemplateToolboxSettings } from 'src/utils/interfaces/export-settings.interface';
import { ProtocolSettings } from 'src/utils/interfaces/settings.interface';
import { measuringRuns, languageItems } from '../../../../../utils/constants/settings.const';
import { TranslateDirective } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'rc-template-toolbox-category-settings',
  templateUrl: './template-toolbox-category-settings.component.html',
  styleUrls: ['./template-toolbox-category-settings.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateDirective,
    SelectV2Module,
    RadioV2Module,
    InputV2Module,
    ToggleV2Module,
    AsyncPipe,
  ],
})
export class TemplateToolboxCategorySettingsComponent implements OnInit {
  private readonly unitsService = inject(UnitsService);

  @Input() public config: ReportTemplateConfiguration;
  public readonly settings = output<TemplateToolboxSettings>();

  public unbalUnits$: Observable<SelectItemV2<UnbalanceUnit>[]>;
  public massUnits$: Observable<SelectItemV2<MassUnit>[]>;
  public lengthUnits$: Observable<SelectItemV2<LengthUnit>[]>;
  public nominalSpeedUnits$: Observable<SelectItemV2<FrequencyUnit>[]>;
  public decimals$: Observable<SelectItemV2<string>[]>;
  public measuringRuns = measuringRuns;
  public languageItems = languageItems;
  public protocolSettings: Partial<ProtocolSettings>;
  public selectSettings: TemplateToolboxSettings = {} as TemplateToolboxSettings;

  public ngOnInit(): void {
    this.getSelectedItems();
    this.setSelectedSettings();
    this.setProtocolSettings();
    this.settings.emit(this.selectSettings);
  }

  public selectedUnitChange(item: SelectItemV2 | string, key: string): void {
    this.selectSettings[key] =
      key === 'decimals' || key === 'measuringRuns'
        ? (item as string)
        : (item as SelectItemV2).value;
    this.settings.emit(this.selectSettings);
  }

  public toggleUnbalanceAsMass(active: boolean): void {
    this.selectSettings.isUnbalanceAsMass = active;
    this.settings.emit(this.selectSettings);
  }

  private setSelectedSettings(): void {
    this.selectSettings = {
      lengthUnit: this.config.lengthUnit ? this.config.lengthUnit : UNIT_DEFAULTS.length,
      weightUnit: this.config.weightUnit ? this.config.weightUnit : UNIT_DEFAULTS.mass,
      unbalUnit: this.config.unbalUnit ? this.config.unbalUnit : UNIT_DEFAULTS.unbalance,
      decimals: this.config.numberOfDecimalPlaces
        ? this.config.numberOfDecimalPlaces
        : UNIT_DEFAULTS.decimals,
      language: this.config.language,
      measuringRuns: this.config.measuringRun,
      isUnbalanceAsMass: this.config.isUnbalanceAsMass
        ? this.config.isUnbalanceAsMass
        : UNIT_DEFAULTS.isUnbalanceAsMass,
      rotationUnit: this.config.rotationUnit ? this.config.rotationUnit : UNIT_DEFAULTS.frequency,
    };

    if (this.config.isUnbalanceAsMass) {
      this.unbalUnits$ = this.unitsService.getSelectItemsByType$('Mass');
    }
  }

  private getSelectedItems(): void {
    this.unbalUnits$ = this.unitsService.getSelectItemsByType$('Unbalance');
    this.massUnits$ = this.unitsService.getSelectItemsByType$('Mass');
    this.lengthUnits$ = this.unitsService.getSelectItemsByType$('Length');
    this.nominalSpeedUnits$ = this.unitsService.getSelectItemsByType$('Frequency');
  }

  private setProtocolSettings(): void {
    this.protocolSettings = {
      measuringRuns: this.config.measuringRun,
      protocolLanguage: this.config.language,
    };
  }
}
